// Phase 129 Plan 06 — DemoHandoffBanner.
//
// Renders ABOVE the active Step1/Step2 card on /w/:ws/sandbox/quick-start
// when an active SandboxSession carries `origin === 'demo_handoff'`. The
// banner explains what was auto-connected (the phone the user demoed with on
// hookmyapp.com) and offers a real escape hatch via a "Change" button that
// flips the page into change-flow mode (`?change=1`) so the user can bind a
// different phone. The auto-connected demo session is NOT removed when the
// user clicks Change — it is only deleted on successful completion of the
// alternate Step-1 bind. Abandoning the change keeps the demo session.
//
// CONTEXT.md amendment: "real escape hatch, not a no-op". The banner exists
// to make the silent demo→signup→workspace handoff explicit and reversible.
//
// Phone formatting: a banner-local helper, NOT the shared `formatSandboxPhone`
// helper. The shared helper produces NANP `+1 415-555-1234` (dashes only),
// but the consumer-friendly contact-style form in the banner is
// `+1 (415) 555-1234`. The shared helper is also designed for raw env-var
// digits (no leading `+`), whereas `session.phone` may already be `+`-prefixed
// (per Phase 127 D1 — sandbox.tsx:551 renders session.phone raw). The local
// helper strips a leading `+`, then dispatches on length.

import { useEffect } from 'react';
import { CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { emit } from '@/lib/analytics';

/**
 * Format a sandbox session's phone for human-friendly banner display.
 *
 *   - 11 digits leading "1"   (NANP/US): `+1 (415) 555-1234`
 *   - 12 digits leading "972" (IL):     `+972 55 704 6276`
 *   - anything else:                     `+${raw}` (lossless fallback)
 *
 * Tolerates both `+`-prefixed (`+14155551234`) and bare (`14155551234`)
 * inputs by stripping a single leading `+` before dispatching on length.
 *
 * NOT exported — keeps the component file's only export the component
 * itself, satisfying the `react-refresh/only-export-components` lint rule.
 */
function formatPhoneForBanner(phone: string): string {
  const digits = phone.startsWith('+') ? phone.slice(1) : phone;
  if (digits.length === 11 && digits.startsWith('1')) {
    return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  if (digits.length === 12 && digits.startsWith('972')) {
    return `+${digits.slice(0, 3)} ${digits.slice(3, 5)} ${digits.slice(5, 8)} ${digits.slice(8)}`;
  }
  return `+${digits}`;
}

interface DemoHandoffBannerProps {
  workspaceId: string;
  sessionPublicId: string;
  /** WA: E.164 digits (with or without leading +). IG: unused. */
  phone: string;
  /** STI discriminator. Defaults to 'whatsapp' for back-compat. */
  senderType?: 'whatsapp' | 'instagram';
  /** IG-only — resolved handle (no leading @). When present, renders
   *  as `@${senderInstagramUsername}`. NULL while the async Meta
   *  Profile API resolver hasn't completed (BOUND payload races the
   *  fire-and-forget lookup); UI falls back to "Instagram account"
   *  in that case. Renamed from `instagramSenderUsername` to match
   *  the senderInstagram* wire pattern. */
  senderInstagramUsername?: string | null;
  onChangeClick: () => void;
}

/**
 * Visual: rounded card aligned with the rest of the Quick Start page (matches
 * Step1/Step2 Card radii at 20px-ish via `rounded-md`). The accent button
 * uses the `text-primary` token (Meta-Blue equivalent in this app's theme).
 *
 * Copy is exact and stable: changing the headline string breaks the Wave-0
 * Playwright matcher (`/We connected the number you tested with/i`).
 *
 * The button label is "Change" (Title Case, single word) followed by the
 * arrow glyph "→". Title Case per project convention; no em dashes per
 * project copy rule.
 *
 * Two events fire from this component:
 *   - `quick_start_consent_banner_shown` once on mount (effect runs only
 *     when ids change — a remount would re-fire, intentional for cases
 *     where the user toggles in/out of change-flow).
 *   - `quick_start_change_clicked` on Change button click, BEFORE the
 *     parent's `onChangeClick` runs so the capture lands even if the
 *     subsequent navigation tears down the page synchronously.
 *
 * Both events fail open (the wrapper's posthog.capture is wrapped in
 * try/catch — analytics.ts).
 */
export function DemoHandoffBanner({
  workspaceId,
  sessionPublicId,
  phone,
  senderType = 'whatsapp',
  senderInstagramUsername,
  onChangeClick,
}: DemoHandoffBannerProps) {
  const isInstagram = senderType === 'instagram';
  const headline = isInstagram
    ? 'We connected the Instagram account you tested with'
    : 'We connected the number you tested with';
  const destination = isInstagram
    ? senderInstagramUsername
      ? `@${senderInstagramUsername}`
      : 'Instagram account'
    : formatPhoneForBanner(phone);
  const changeButtonLabel = isInstagram ? 'Change Account' : 'Change Number';
  const connectedLabel = isInstagram ? 'Instagram account' : 'Phone number';
  useEffect(() => {
    emit('quick_start_consent_banner_shown', {
      workspace_id: workspaceId,
      session_public_id: sessionPublicId,
    });
  }, [workspaceId, sessionPublicId]);

  function handleClick() {
    emit('quick_start_change_clicked', {
      workspace_id: workspaceId,
      session_public_id: sessionPublicId,
    });
    onChangeClick();
  }

  return (
    <div
      role="status"
      className="flex flex-col items-center gap-3 rounded-2xl border bg-card px-5 py-3.5 sm:flex-row sm:items-center sm:justify-between sm:gap-4"
    >
      <div className="flex min-w-0 flex-col items-center gap-3 sm:flex-row sm:items-center">
        <div
          aria-hidden="true"
          className="flex size-8 shrink-0 items-center justify-center rounded-full bg-primary/10"
        >
          <CheckCircle2 className="size-4 text-primary" strokeWidth={2.5} />
        </div>
        <div className="min-w-0 text-center sm:text-left">
          <p className="text-xs text-muted-foreground">
            Connected · {connectedLabel}
          </p>
          <p
            className={`mt-0.5 truncate text-base font-semibold leading-tight text-foreground ${
              isInstagram ? '' : 'tabular-nums'
            }`}
            title={destination}
          >
            {destination}
          </p>
          <span className="sr-only">{headline}</span>
        </div>
      </div>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={handleClick}
        className="shrink-0 rounded-full"
      >
        {changeButtonLabel}
      </Button>
    </div>
  );
}
